// ÁTTEKINTÉS — „Aktuális megbeszélések" kártya, AGENDA-nézetben.
//
// A LEGACY-BŐL ÁTVETT VISELKEDÉS: a MAI naptól előre a következő pár megbeszélés, résztvevő-avatarokkal, és —
// ha én is résztvevő vagyok — INLINE RSVP („Ott leszek / Nem megyek"). A legacy ugyanezt a gombokat tette a sor
// végére; a válasz azonnal látszik (optimista), a lista-invalidálás a forrás-igazság.
//
// A DESIGN (user-döntés): a korábbi egyszerű lista helyett AGENDA — NAP-SZEPARÁTOR („ma" / „holnap"
// / dátum), bal oldalon IDŐ-OSZLOP (tabular-nums, jobbra zárva), mellette függőleges IDŐ-SÁV a helyszín
// SZÍNÉVEL, és a folyamatban lévő megbeszélésen „most" jelölés. Miért jobb: a kezdőlapon a kérdés nem az, hogy
// „milyen sorok vannak", hanem hogy „mikor mi jön" — az idő-oszlop és a nap-szeparátor ezt egy pillantásra adja,
// a szín pedig ugyanaz a kulcs, amit a naptár-nézet használ (egy vizuális nyelv két felületen).
//
// A SZÍN a naptár esemény-palettájának `light.border` hue-ja (`MEETING_EVENT_COLORS`) — SZÁNDÉKOSAN
// téma-független: ez egy 8 px-es pont/vonal, aminek mindkét témán ugyanaz a hue-ja (a paletta közepes
// szaturációjú keretszínei világos és sötét háttéren is olvashatók), így a kártyának nincs téma-állapota.
import { MapPinIcon, UsersIcon } from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { DomainBadge } from '@/components/shared/domain-badge';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { PanelCard, PanelCardEmpty } from '@/components/shared/panel-card';
import { PersonChip } from '@/components/shared/person-chip';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import type { DashboardMeeting } from '@/features/dashboard/use-dashboard-data';
import { MEETING_EVENT_COLORS, locationColorKey } from '@/features/meetings/calendar-map';
import { useMeetingRsvp } from '@/features/meetings/use-meeting-rsvp';
import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';

/** A helyszín-kulcs jelző-színe (a naptár palettájából; lásd a fájl-fejléc magyarázatát). */
function railColor(locationKey: string | null): string {
  return (
    MEETING_EVENT_COLORS[locationColorKey(locationKey)]?.light.border ??
    MEETING_EVENT_COLORS.other!.light.border
  );
}

/** Egy megbeszélés a saját napján belül: idő-oszlop + szín-sáv + tartalom + (résztvevőként) RSVP. */
function MeetingRow({
  meeting,
  currentUserId,
  isNow,
}: {
  meeting: DashboardMeeting;
  currentUserId: string | null;
  isNow: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const mine =
    currentUserId === null
      ? null
      : (meeting.participants.find((p) => p.id === currentUserId) ?? null);
  const { myStatus, isPending, setRsvp } = useMeetingRsvp(meeting.id, mine?.status ?? null);

  const start = meeting.startsAt;
  const startTime = start
    ? formatDateTime(start, i18n.language, DEFAULT_TIME_ZONE, { timeStyle: 'short' })
    : '';
  const endTime = meeting.endsAt
    ? formatDateTime(meeting.endsAt, i18n.language, DEFAULT_TIME_ZONE, { timeStyle: 'short' })
    : '';
  const color = railColor(meeting.locationKey);

  return (
    // A SOR egésze kattintható (a cím-link kifeszített rétege): a Megbeszélések naptára a megbeszélés NAPJÁN nyílik,
    // és rögtön megnyitja (szerkesztő, vagy jog nélkül a részlet-kártya). Az RSVP-gombok és a résztvevő-lenyitó
    // a réteg FÖLÖTT állnak, tehát a saját dolgukat csinálják.
    <CardRow className="hover:bg-accent/60 relative flex gap-3 px-4 py-3 transition-colors">
      {/* IDŐ-OSZLOP: fix szélesség + jobbra zárás → az időpontok egymás alatt tömbben állnak. */}
      <div className="w-14 shrink-0 pt-0.5 text-right">
        <div className="text-md text-foreground font-medium tabular-nums">{startTime}</div>
        {endTime !== '' && (
          <div className="text-muted-foreground text-xs tabular-nums">{endTime}</div>
        )}
      </div>

      {/* IDŐ-SÁV: a helyszín színe adja a vonalat és a pontot; a folyamatban lévőn a pont tele van. */}
      <div className="relative flex w-3 shrink-0 justify-center" aria-hidden>
        <span className="bg-border absolute inset-y-0 w-px" />
        <span
          className="relative mt-1.5 size-2.5 rounded-full border-2 bg-[color:var(--card)]"
          style={{
            borderColor: color,
            backgroundColor: isNow ? color : undefined,
          }}
        />
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link
            to="/meetings"
            search={{
              open: meeting.id,
              ...(meeting.startsAt === null ? {} : { date: dayKey(meeting.startsAt) }),
            }}
            className="focus-visible:ring-ring min-w-0 rounded-sm after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2"
          >
            <Text variant="itemTitle" as="span" className="block truncate">
              {meeting.title || t('dashboard.meetings.untitled')}
            </Text>
          </Link>
          {isNow && <DomainBadge kind="marker" value="now" label={t('dashboard.meetings.now')} />}
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {meeting.locationKey !== null && meeting.locationKey !== '' && (
            <span className="inline-flex items-center gap-1">
              <MapPinIcon className="size-3.5" aria-hidden />
              {t(`meetings.locationLabel.${meeting.locationKey}`, {
                defaultValue: meeting.locationKey,
              })}
            </span>
          )}
          {meeting.participants.length > 0 && (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <UsersIcon className="size-3.5" aria-hidden />
              {t('dashboard.meetings.participantCount', { count: meeting.participants.length })}
            </span>
          )}
        </div>
        {meeting.participants.length > 0 && (
          <div className="relative z-10">
            <ExpandableCell
              items={meeting.participants}
              getKey={(p) => p.id}
              visible={3}
              threshold={4}
              moreLabel={(n) => t('meetings.more', { count: n })}
              triggerAriaLabel={t('meetings.moreParticipants')}
              renderItem={(p) => (
                <PersonChip
                  name={p.name ?? t('common.deletedUser')}
                  givenName={p.givenName}
                  familyName={p.familyName}
                  avatarUrl={p.avatarUrl}
                  accentColor={p.teamColor}
                  size="sm"
                  className="gap-1.5 text-xs"
                />
              )}
            />
          </div>
        )}
        {/* SAJÁT RSVP — csak akkor, ha résztvevő vagyok (a legacy is csak a résztvevőnek adta a gombokat). */}
        {mine !== null && (
          <div className="relative z-10 flex flex-wrap items-center gap-1.5 pt-0.5">
            <Button
              type="button"
              size="sm"
              variant={myStatus === 'confirmed' ? 'brand' : 'outline'}
              disabled={isPending}
              onClick={() => setRsvp('confirmed')}
            >
              {t('meetings.rsvp.actionConfirm')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={myStatus === 'declined' ? 'destructive' : 'outline'}
              disabled={isPending}
              onClick={() => setRsvp('declined')}
            >
              {t('meetings.rsvp.actionDecline')}
            </Button>
          </div>
        )}
      </div>
    </CardRow>
  );
}

/**
 * A megjelenítési zóna szerinti NAP-KULCS (`YYYY-MM-DD`).
 *
 * MIÉRT nem a `formatDateTime`: az a `language`-et MEGJELENÍTÉSI locale-ra képezi (`hu-HU`/`en-GB`), tehát
 * `dd/mm/yyyy`-t is adhat — gép-olvasható kulcshoz az `en-CA` (ISO-alakú) formázó kell, fixen.
 */
function dayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DEFAULT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** Nap-csoportok a kezdés NAPJA szerint (a sorrend a lista sorrendje — a szerver kezdés szerint rendez). */
function groupByDay(meetings: DashboardMeeting[]): { day: string; items: DashboardMeeting[] }[] {
  const groups: { day: string; items: DashboardMeeting[] }[] = [];
  for (const m of meetings) {
    // A NAP a megjelenítési zónában dől el (`Europe/Budapest`) — nem az UTC-nap, különben a késő esti
    // megbeszélés „másnapra" csúszna a szeparátorban.
    const day = m.startsAt === null ? '' : dayKey(m.startsAt);
    const last = groups.at(-1);
    if (last?.day === day) last.items.push(m);
    else groups.push({ day, items: [m] });
  }
  return groups;
}

/** A kártya legfeljebb ennyi (egymást követő, megbeszélést tartalmazó) napot mutat; a többi a „Mind megnyitása” mögött. */
export const MEETINGS_MAX_DAYS = 3;

export interface UpcomingMeetingsCardProps {
  meetings: DashboardMeeting[] | null;
  loading: boolean;
  currentUserId: string | null;
}

/**
 * A fejlécben NINCS „Új megbeszélés" gomb (user-döntés): a létrehozás a „Gyors műveletek"
 * kártyán él, egy helyen az összes modul create-akciójával — így a kártya-fejlécek egyforma magasak, és a
 * create nem szóródik szét a kezdőlapon.
 */
export function UpcomingMeetingsCard({
  meetings,
  loading,
  currentUserId,
}: UpcomingMeetingsCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const now = Date.now();

  /** A nap-szeparátor felirata: „ma" / „holnap" relatívan, különben a teljes dátum. */
  const dayLabel = (day: string): string => {
    if (day === '') return t('dashboard.meetings.untitledDay');
    // A 12:00 UTC azért van, hogy a nap-kulcsból épített instant egyik zónában se csússzon át másik napra.
    const isoDay = `${day}T12:00:00.000Z`;
    const today = dayKey(new Date().toISOString());
    const tomorrow = dayKey(new Date(Date.now() + 86_400_000).toISOString());
    const full = formatDateTime(isoDay, i18n.language, DEFAULT_TIME_ZONE, {
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
    if (day === today) return `${t('dashboard.meetings.today')} · ${full}`;
    if (day === tomorrow) return `${t('dashboard.meetings.tomorrow')} · ${full}`;
    return full;
  };

  const groups = meetings === null ? [] : groupByDay(meetings).slice(0, MEETINGS_MAX_DAYS);

  return (
    <PanelCard
      capped
      title={t('dashboard.meetings.title')}
      openAllTo="/meetings"
      loading={loading}
      skeletonRows={3}
    >
      {groups.length === 0 ? (
        <PanelCardEmpty>{t('dashboard.meetings.empty')}</PanelCardEmpty>
      ) : (
        <div>
          {groups.map((g) => (
            <section key={g.day || 'unknown'}>
              <Text variant="overline" as="h3" className="bg-muted/40 px-4 py-1.5">
                {dayLabel(g.day)}
              </Text>
              <CardRows>
                {g.items.map((m) => (
                  <MeetingRow
                    key={m.id}
                    meeting={m}
                    currentUserId={currentUserId}
                    isNow={
                      m.startsAt !== null &&
                      Date.parse(m.startsAt) <= now &&
                      (m.endsAt === null || Date.parse(m.endsAt) >= now)
                    }
                  />
                ))}
              </CardRows>
            </section>
          ))}
        </div>
      )}
    </PanelCard>
  );
}
